import {FC, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {
    ArrowDownwardOutlined,
    ArrowUpwardOutlined,
    TableChartOutlined,
    UnfoldMoreOutlined,
} from '@mui/icons-material';
import {useIntl} from 'react-intl';
import {AiAgentCustomTableData} from 'src/types';
import {compareCells, formatCell} from '../utils/formatCell';
import {buildCsv, downloadCsv} from '../utils/csvExport';
import {TableToolbar} from './TableToolbar';
import {customTableSx} from './CustomTable.sx';

interface CustomTableProps {
    data: AiAgentCustomTableData;
}

type SortDir = 'asc' | 'desc';

interface SortState {
    key: string;
    dir: SortDir;
}

export const CustomTable: FC<CustomTableProps> = ({data}) => {
    const intl = useIntl();
    const {title, columns, rows, footer} = data;

    const [sort, setSort] = useState<SortState | null>(null);
    const [search, setSearch] = useState('');

    const hasSearchableColumn = useMemo(
        () => columns.some((c) => c.format === 'text'),
        [columns]
    );

    const filteredRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return rows;
        const textKeys = columns.filter((c) => c.format === 'text').map((c) => c.key);
        if (textKeys.length === 0) return rows;
        return rows.filter((row) =>
            textKeys.some((k) => {
                const v = row[k];
                return v != null && String(v).toLowerCase().includes(term);
            })
        );
    }, [rows, columns, search]);

    const sortedRows = useMemo(() => {
        if (!sort) return filteredRows;
        const col = columns.find((c) => c.key === sort.key);
        if (!col) return filteredRows;
        const copy = [...filteredRows];
        copy.sort((a, b) => {
            const cmp = compareCells(a[sort.key], b[sort.key], col.format);
            return sort.dir === 'asc' ? cmp : -cmp;
        });
        return copy;
    }, [filteredRows, sort, columns]);

    const handleSort = (key: string): void => {
        setSort((prev) => {
            if (!prev || prev.key !== key) return {key, dir: 'asc'};
            if (prev.dir === 'asc') return {key, dir: 'desc'};
            return null;
        });
    };

    const handleExport = (): void => {
        const csv = buildCsv(
            columns.map((c) => ({key: c.key, label: c.label})),
            sortedRows,
            (row, key) => {
                const col = columns.find((c) => c.key === key);
                return col ? formatCell(row[key], col.format) : String(row[key] ?? '');
            }
        );
        downloadCsv(title, csv);
    };

    const rowCount = sortedRows.length;

    return (
        <Box sx={customTableSx.root}>
            <TableToolbar
                icon={<TableChartOutlined/>}
                title={title}
                totalCount={rows.length}
                searchEnabled={hasSearchableColumn}
                searchValue={search}
                onSearchChange={setSearch}
                onExport={handleExport}
                exportDisabled={rowCount === 0}
            />
            <Box sx={customTableSx.scrollArea}>
                <Box component="table" sx={customTableSx.table}>
                    <Box component="thead" sx={customTableSx.thead}>
                        <Box component="tr">
                            {columns.map((col) => {
                                const isActive = sort?.key === col.key;
                                const Icon = !isActive
                                    ? UnfoldMoreOutlined
                                    : sort?.dir === 'asc'
                                        ? ArrowUpwardOutlined
                                        : ArrowDownwardOutlined;
                                return (
                                    <Box
                                        component="th"
                                        key={col.key}
                                        sx={{
                                            ...customTableSx.thSortable,
                                            textAlign: col.align ?? 'left',
                                        }}
                                        onClick={() => handleSort(col.key)}
                                    >
                                        <Box component="span" sx={customTableSx.thInner}>
                                            {col.label}
                                            <Icon
                                                sx={
                                                    isActive
                                                        ? customTableSx.sortIconActive
                                                        : customTableSx.sortIcon
                                                }
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {rowCount === 0 ? (
                            <Box component="tr">
                                <Box
                                    component="td"
                                    colSpan={columns.length}
                                    sx={customTableSx.emptyRow}
                                >
                                    {intl.formatMessage({id: 'aiAgentTableNoResults'})}
                                </Box>
                            </Box>
                        ) : (
                            sortedRows.map((row, idx) => {
                                const isLast = idx === rowCount - 1;
                                return (
                                    <Box component="tr" key={idx}>
                                        {columns.map((col) => (
                                            <Box
                                                component="td"
                                                key={col.key}
                                                sx={{
                                                    ...(isLast
                                                        ? {...customTableSx.td, ...customTableSx.tdLast}
                                                        : customTableSx.td),
                                                    textAlign: col.align ?? 'left',
                                                    fontVariantNumeric:
                                                        col.format === 'text' || col.format === 'date'
                                                            ? undefined
                                                            : 'tabular-nums',
                                                }}
                                            >
                                                {formatCell(row[col.key], col.format)}
                                            </Box>
                                        ))}
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Box>
            </Box>
            {footer && (
                <Box sx={customTableSx.footer}>
                    <Typography sx={customTableSx.footerLabel}>{footer}</Typography>
                </Box>
            )}
        </Box>
    );
};
